/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { set, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { debounce } from '@ember/runloop';
import { isEmpty } from '@ember/utils';
import ContactType from 'linced-app/enums/contact-type';
import { serializeQueryParams } from 'linced-app/utils/url';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import RelationshipEnum from '@linc-technologies/linced-common/enums/contact-relationship';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import {
  findBy,
  resolved,
} from '@linc-technologies/linced-common/utils/arrays';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import ENV from 'linced-app/config/environment';
import { restartableTask } from 'ember-concurrency';
import moment from 'moment';
import { observes } from '@ember-decorators/object';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default class ContactManager extends Component {
  @action
  setContactAllowedAccess(value) {
    this.contactAllowedAccess = value;
  }

  @action
  setContactEmail(value) {
    this.contactEmail = value;
  }

  @action
  setContactFirstName(value) {
    this.contactFirstName = value;
  }

  @action
  setContactLastName(value) {
    this.contactLastName = value;
  }

  @action
  setContactNotes(value) {
    this.contactNotes = value;
  }

  @action
  setContactPriority(value) {
    this.contactPriority = value;
  }

  @action
  setContactReceivesSMS(value) {
    this.contactReceivesSMS = value;
  }

  @action
  setContactRelationship(value) {
    this.contactRelationship = value;
  }

  @action
  setContactType(value) {
    this.contactType = value;
  }

  @action
  setSearchMode(value) {
    this.searchMode = value;
  }

  @action
  setSecondaryContactsExpanded(value) {
    this.secondaryContactsExpanded = value;
  }

  // Services
  @service
  fetch;

  @service
  options;

  @service
  permissions;

  @service
  schoolSchema;

  @service
  session;

  @service
  store;

  @service
  toaster;

  ContactType = ContactType;
  Jurisdiction = Jurisdiction;
  @tracked readOnly = false;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('person.learner.contacts.@each.hasCustodyOrder')
  get hasCustodyOrder() {
    if (isEmpty(this.get('person.learner.contacts'))) {
      return false;
    }
    return this.get('person.learner.contacts').any(
      (contact) => contact.hasCustodyOrder,
    );
  }

  @computed('readOnly', 'jurisdiction', 'hasCustodyOrder')
  get readOnlyContacts() {
    if (this.readOnly) {
      // Don't argue with the attribute.
      return true;
    }

    // Check if we're in a jurisdiction which doesn't allow changes.
    switch (this.jurisdiction) {
      case Jurisdiction.AU_NSW:
        // NSW schools can't edit contacts in Hero - changes must be made in
        // ERN, and synced into Hero via SAIS.
        return true;
    }

    // Otherwise, assume that changes are allowed.
    return false;
  }

  // canAddContacts determines whether a school can add new contacts for caregivers.
  @computed('readOnlyContacts', 'jurisdiction', 'session.scopes.[]')
  get canAddContacts() {
    // Check if we're in a jurisdiction which doesn't allow it.
    switch (this.jurisdiction) {
      case Jurisdiction.AU_NSW:
        // NSW schools cannot add new contacts no matter what.
        return false;
    }

    // Otherwise, calculate based on whether the contacts are readOnly & user is TENANT ADMIN.
    return !this.readOnlyContacts && this.permissions.meet(Scopes.TENANT_ADMIN);
  }

  // heroAccessIsReadOnly determines whether to make the Hero access slider
  // read-only for the contact currently being edited.
  // NOTE: This logic may require adjustment for any additional jurisdictions
  // which support secondary contacts (and are also read-only).
  @computed(
    'readOnlyContacts',
    'hasCustodyOrder',
    'jurisdiction',
    'contact.type',
    'person.accessArrangements',
  )
  get heroAccessIsReadOnly() {
    if (!this.readOnlyContacts) {
      // Contacts aren't read-only, so Hero access shouldn't be either.
      return false;
    }

    switch (this.jurisdiction) {
      case Jurisdiction.AU_NSW:
        // NSW schools can (and must) manage Hero access for students with
        // custody orders and/or Legal Dossiers, as we don't have sufficient
        // information to safely determine who should have access in these
        // situations.  The "access arrangements" field is read-only, and
        // a generated note is added to it if a Legal Dossier is present.
        if (
          this.hasCustodyOrder ||
          !isEmpty(this.get('person.accessArrangements'))
        ) {
          return false;
        }
        // Primary and secondary contacts of students without custody orders
        // or Legal Dossiers always have access, and the school shouldn't be
        // able to turn it off.
        return true;
    }
  }

  @computed('jurisdiction')
  get useSecondaryContacts() {
    return this.jurisdiction === Jurisdiction.AU_NSW;
  }

  @tracked
  secondaryContactsExpanded = false;

  @alias('person.learner.secondaryContacts.length')
  numSecondaryContacts;

  @computed('person.learner.secondaryContacts.@each.allowedAccess')
  get hasSecondaryContactWithAccess() {
    if (isEmpty(this.get('person.learner.secondaryContacts'))) {
      return false;
    }
    return this.get('person.learner.secondaryContacts').some(
      (contact) => contact.allowedAccess,
    );
  }

  @computed('fieldGroups.[]')
  get groups() {
    // 3 == TYPE_CAREGIVER
    return this.fieldGroups.filter((group) => group.personType === 3);
  }

  @computed('person.firstName')
  get contactRelationshipLabel() {
    return 'Relationship to ' + this.get('person.firstName');
  }

  @computed('groups.[]')
  get relationshipGroup() {
    return this.groups.find((group) => group.key === 'relationship');
  }

  // relationshipGroupFields resolves relationshipGroup.fields to its loaded
  // records: that hasMany is a PromiseManyArray, and iterating it directly
  // (e.g. a template {{#each}}) is deprecated.
  @computed('relationshipGroup.fields.@each.key')
  get relationshipGroupFields() {
    return resolved(this.relationshipGroup?.fields);
  }

  @computed('groups.[]')
  get caregiverFieldGroups() {
    return this.groups.filter((group) => group.key !== 'relationship');
  }

  @tracked
  contactDialogOpen = false;
  @tracked
  contactDetailsOpen = false;
  @tracked
  editingContact = false;
  @tracked
  contactType = 1;
  @tracked
  contactPriority = 0;
  @tracked
  contactRelationship = null;
  @tracked
  contactNotes = '';
  @tracked contactAllowedAccess = false;
  @tracked
  contactReceivesSMS = false;
  @tracked
  selectedContact = null;
  @tracked contact = null;
  @tracked
  contactToRemove = null;
  @tracked
  confirmRemoveContactDialogOpen = false;
  @tracked searchMode = 'email';
  @tracked contactEmail = '';
  @tracked contactFirstName = '';
  @tracked contactLastName = '';
  @tracked
  searchResults = [];
  @tracked
  conflictedEmailAddress = '';
  relationshipTypes = RelationshipEnum.values;

  @computed('useSecondaryContacts')
  get contactTypes() {
    if (this.useSecondaryContacts) {
      return [
        {
          value: ContactType.PRIMARY,
          label: 'Primary Carer (Residential)',
        },
        {
          value: ContactType.SECONDARY,
          label: 'Secondary Carer (Non-Residential)',
        },
        {
          value: ContactType.EMERGENCY,
          label: 'Additional / Emergency Contact',
        },
      ];
    }
    return [
      {
        value: ContactType.PRIMARY,
        label: 'Primary Carer',
      },
      {
        value: ContactType.EMERGENCY,
        label: 'Additional / Emergency Contact',
      },
    ];
  }

  @observes('contactEmail')
  _emailChanged() {
    if (this.contactEmailValid) {
      // Set "searching" flag so that we don't get a flash of "no results found" before the debounced search.
      this.searching = true;
    }
    debounce(this, this.searchContacts, 350);
  }

  @observes('contactFirstName', 'contactLastName')
  _noEmailChanged() {
    if (this.noEmailSearchValid) {
      // Set "searching" flag so that we don't get a flash of "no results found" before the debounced search.
      this.searching = true;
    }
    debounce(this, this.searchContacts, 350);
  }

  @computed('contactEmail')
  get contactEmailValid() {
    return emailPattern.test(this.contactEmail);
  }

  // We can perform a search if we have at least one name.
  @computed('contactFirstName', 'contactLastName')
  get noEmailSearchValid() {
    return !isEmpty(this.contactFirstName) || !isEmpty(this.contactLastName);
  }

  get searchIsValid() {
    switch (this.searchMode) {
      case 'email':
        return this.contactEmailValid;
      case 'name':
        return this.noEmailSearchValid;
      default:
        return false;
    }
  }

  contactEmailValidation = Object.freeze([
    {
      message: 'Please enter a valid email address',
      validate: (val) => {
        return isEmpty(val) || emailPattern.test(val);
      },
    },
  ]);

  @optionAlias('nzMOE:boardElections:mainRollClosingDate')
  mainRollClosingDate;
  @optionAlias('nzMOE:boardElections:supplementaryRollClosingDate')
  supplemantryRollClosingDate;

  @tracked
  searching;

  // closedRollTask finds if the contact is on a closed roll for the board
  // elections. The actions that set `contact` perform it.
  closedRollTask = restartableTask(async () => {
    if (
      this.get('person.learner.enrolmentStub.status') !==
        EnrolmentStatus.CURRENT ||
      !this.contact?.get('fields.nzMOE:botVoting.value') ||
      isEmpty(this.mainRollClosingDate)
    ) {
      return false;
    }

    if (!isEmpty(this.supplemantryRollClosingDate)) {
      return true;
    }

    try {
      let enrolment = await this.get('person.enrolment');
      if (
        moment
          .unix(enrolment.get('updateTime'))
          .isAfter(this.mainRollClosingDate)
      ) {
        return false;
      }

      let updateDate = this.contact.get(
        'fields.nzMOE:botVotingUpdateDate.value',
      );
      return (
        isEmpty(updateDate) ||
        moment(updateDate).isBefore(this.mainRollClosingDate)
      );
    } catch {
      // Do not lock the field when the enrolment does not load.
      return false;
    }
  });

  // isOnClosedRoll is false until closedRollTask finds the value for the
  // current contact.
  get isOnClosedRoll() {
    return this.closedRollTask.last?.value ?? false;
  }

  @computed('contact.allowedAccess', 'contactAllowedAccess')
  get losingHeroAccess() {
    return this.contact.allowedAccess && !this.contactAllowedAccess;
  }

  @computed('contact.type', 'contactType.value')
  get becomingEmergencyContact() {
    return (
      this.contact.type === ContactType.PRIMARY &&
      this.contactType.value === ContactType.EMERGENCY
    );
  }

  @action
  addContact(contactType) {
    this.setProperties({
      contactType: findBy(this.contactTypes, 'value', contactType),
      contactDialogOpen: true,
      editingContact: false,
      searchMode: 'email',
      contactEmail: '',
      contactFirstName: '',
      contactLastName: '',
      contactRelationship: null,
      conflictedEmailAddress: '',
    });
  }

  @action
  editContact(contact) {
    let selectedContact = this.store.peekRecord(
      'person',
      contact.get('caregiverId'),
    );
    this.selectedContact = selectedContact;
    this.contactRelationship = findBy(
      this.relationshipTypes,
      'value',
      contact.get('relationship'),
    );
    this.contactType = findBy(this.contactTypes, 'value', contact.get('type'));
    this.contactPriority = contact.get('priority');
    this.contactNotes = contact.get('notes');
    this.contactAllowedAccess = contact.get('allowedAccess');
    this.contactReceivesSMS = contact.get('receivesSms');
    this.contact = contact;
    this.closedRollTask.perform();
    this.editingContact = true;

    this.contactDialogOpen = true;
  }

  @action
  closeContactDialog() {
    // A rollback also stops the save of the form defaults of the contact.
    this.contact?.rollbackAttributes();
    this.contactDialogOpen = false;
    this.resetForm();
  }

  @action
  saveContact() {
    let contact = this.contact;

    // Disable bill payer flag if the contact is losing Hero access, or being
    // changed from a Primary contact to an Emergency contact.
    if (this.losingHeroAccess || this.becomingEmergencyContact) {
      contact.set('billPayer', false);
    }

    // Set the bulk of the properties.
    contact.setProperties({
      relationship: this.get('contactRelationship.value'),
      type: this.contactType.value,
      notes: this.contactNotes,
      priority: this.contactPriority,
      allowedAccess: this.contactAllowedAccess,
      receivesSms: this.contactReceivesSMS,
    });

    //Only if main roll is closed, record lstupdate date for contacts whose voting rights are turning on fields.nzMOE:botVotingUpdateDate
    if (contact.get('fields.nzMOE:botVoting')) {
      let botVotingChanged = contact.get(
        'fields.nzMOE:botVoting.hasDirtyAttributes',
      );
      if (
        !isEmpty(this.mainRollClosingDate) &&
        botVotingChanged &&
        contact.get('fields.nzMOE:botVoting.value')
      ) {
        contact.set('fields.nzMOE:botVotingUpdateDate.value', moment());
      } else if (botVotingChanged) {
        contact.set('fields.nzMOE:botVotingUpdateDate.value', null);
      }
    }
    this.get('person.learner.contacts').addFragment(this.contact);
    this.contactDialogOpen = false;
    this.resetForm();
  }

  @action
  newContact() {
    let person = this.schoolSchema.newPerson(PersonType.CAREGIVER);
    switch (this.searchMode) {
      case 'email':
        if (isEmpty(this.conflictedEmailAddress)) {
          person.addField('core:emailAddress', DataType.STRING);
          person
            .get('fields.core:emailAddress')
            .set('value', this.contactEmail);
        }
        break;
      case 'name':
        if (!isEmpty(this.contactFirstName)) {
          person.addField('core:firstName', DataType.STRING);
          person
            .get('fields.core:firstName')
            .set('value', this.contactFirstName);
        }
        if (!isEmpty(this.contactLastName)) {
          person.addField('core:lastName', DataType.STRING);
          person.get('fields.core:lastName').set('value', this.contactLastName);
        }
        break;
    }
    this.selectedContact = person;
    this.contactDetailsOpen = true;
  }

  @action
  handleError(contact, err) {
    let message =
      'Failed to save contact.  Please check the details and try again.';
    if (err?.errors?.[0]?.status === '409') {
      if (!isEmpty(this.conflictedEmailAddress)) {
        message =
          'A matching contact already exists.  Please cancel and try searching again, ' +
          'choosing "This person doesn\'t have an email address".';
      } else {
        message =
          'A matching contact already exists.  Please cancel and try searching again.';
      }
    }

    this.toaster.error(message);
  }

  @action
  enableCaregiverTenantAccess(result) {
    set(result, 'enableInProgress', true);
    let url = `${ENV.APP.apiHost}/people/v4/caregivers/${result.personId}/:enableTenantAccess`;
    let data = {
      personId: result.personId,
      schoolId: this.get('session.activeSchool'),
      learnerId: this.get('person.id'),
    };
    let options = {
      data: JSON.stringify(data),
    };
    return this.fetch.post(url, options).catch(() => {
      // Flag this email address as conflicted, so we can disallow it.
      this.conflictedEmailAddress = result.emailAddress;
      set(result, 'enableFailed', true);
    });
  }

  @action
  selectSearchResult(result) {
    this.store
      .findRecord('person', result.personId)
      .then((contact) => this.selectContact(contact));
  }

  @action
  showConfirmRemoveContact(contact) {
    this.contactToRemove = contact;
    this.confirmRemoveContactDialogOpen = true;
  }

  @action
  confirmRemoveContact(shouldRemove) {
    if (shouldRemove) {
      let contact = this.contactToRemove;
      this.get('person.learner.contacts').removeFragment(contact);
    }
    this.contactToRemove = null;
    this.confirmRemoveContactDialogOpen = false;
  }

  @action
  reset() {
    this.resetSearchResults();
    this.resetForm();
    this.resetSearchForm();
  }

  @action
  closeContactDetails() {
    this.contactDetailsOpen = false;
  }

  searchContacts() {
    let params = {};
    if (this.searchMode === 'email' && this.contactEmailValid) {
      params.query = this.contactEmail.toLowerCase().trim();
    } else if (this.searchMode === 'name' && this.noEmailSearchValid) {
      params = {
        first: this.contactFirstName,
        last: this.contactLastName,
        jurisdiction: this.person.jurisdiction,
      };
    } else {
      this.resetSearchResults();
      return;
    }

    this.searching = true;
    this.fetch
      .request(
        `${ENV.APP.apiHost}/people/v4/caregivers/:search?${serializeQueryParams(
          params,
        )}`,
      )
      .then((response) => {
        if (!!response && !!response.caregivers && response.caregivers.length) {
          this.searchResults = response.caregivers.filter(
            this.filterResults,
            this,
          );
          this.searching = false;
        } else {
          this.resetSearchResults();
        }
      });
  }

  resetSearchResults() {
    this.searchResults = [];
    this.searching = false;
    this.conflictedEmailAddress = '';
  }

  resetSearchForm() {
    this.setProperties({
      searchMode: 'email',
      contactEmail: '',
      contactFirstName: '',
      contactLastName: '',
    });
  }

  resetForm() {
    this.contactType = 1;
    this.contactPriority = 0;
    this.contactRelationship = 0;
    this.contactNotes = '';
    this.contactAllowedAccess = false;
    this.contactReceivesSMS = false;
    this.selectedContact = null;
    this.contact = null;
    this.conflictedEmailAddress = '';
  }

  @action
  selectContact(contact) {
    let fragment = this.store.createFragment('people/contact', {
      caregiverId: contact.get('id'),
      type: this.contactType.value,
    });
    this.contact = fragment;
    this.closedRollTask.perform();
    this.selectedContact = contact;
    this.closeContactDetails();
  }

  filterResults(result) {
    if (
      this.searchMode === 'name' &&
      !result.inThisSchool &&
      isEmpty(result.emailAddress)
    ) {
      // Don't display contacts found by legacyContactId who aren't already associated with this school.
      // We can't send them an access request, as they'd have no way to approve it - so we'll create a new Person.
      return false;
    }
    // Include only if not found in current list of contacts
    return !findBy(
      this.get('person.learner.contacts'),
      'caregiverId',
      result.personId,
    );
  }
}
